'use client'

import { forwardRef } from 'react'

import { LuLoaderCircle } from 'react-icons/lu'

import { cn } from '@/core/utils/common'
import { useHydrated } from '@/core/hooks/use-hydrated'

import type { ButtonHTMLAttributes } from 'react'

type ButtonVariant =
  'primary' | 'accent' | 'secondary' | 'ghost' | 'danger' | 'choice' | 'bare'
type ButtonSize = 'sm' | 'md' | 'lg' | 'icon' | 'icon-sm'
type ButtonShape = 'field' | 'pill'

type ButtonStyleOptions = {
  variant?: ButtonVariant
  size?: ButtonSize
  /** pill = 알약(rounded-full, 기본 — 2026-10-01 veluvit 결정), field = 입력칸과 같은 둥글기(rounded-field) */
  shape?: ButtonShape
  /** 글자를 굵게 — 화면의 주 행동 버튼 */
  bold?: boolean
  skin?: 'default' | 'flat'
  className?: string
}

type ButtonProps = ButtonHTMLAttributes<HTMLButtonElement> &
  Omit<ButtonStyleOptions, 'className'> & {
    loading?: boolean
  }

// veluvit 팔레트(design-guide.md §1): 주 버튼 = 흰 채움 + 검정 글자(2026-10-01 — 코랄 채움 버튼이 촌스럽다는 판정, 애플 TV · 넷플릭스 결).
// 코랄은 버튼 면이 아니라 작은 강조(밑줄 · 배지 · 숫자)에만. accent = 금색(보상)
const variantStyles: Record<Exclude<ButtonVariant, 'bare'>, string> = {
  primary: 'bg-foreground text-background hover:brightness-110',
  accent: 'bg-sub text-inverse hover:bg-sub/90',
  secondary: 'bg-surface-raised text-foreground hover:bg-surface-strong',
  ghost: 'text-foreground hover:bg-surface-strong',
  choice:
    'border border-foreground/10 bg-foreground/[0.025] text-foreground hover:border-foreground/25 hover:bg-foreground/[0.05] aria-pressed:border-point/60 aria-pressed:bg-point/[0.08] aria-pressed:hover:bg-point/[0.12] focus-visible:border-point focus-visible:bg-point/10',
  danger: 'bg-error text-inverse hover:bg-error/90',
}

// 비활성 — 채운 버튼(코랄 · 금 · 빨강)은 반투명으로 흐리면 탁한 갈색이 돼 읽히지 않는다. 애플처럼 회색 면 + 흐린 글자로 바꾼다.
// 나머지(면 없는 버튼)는 투명도로 흐린다
const FILLED_DISABLED =
  'disabled:bg-surface-strong disabled:text-description disabled:hover:bg-surface-strong'
const disabledStyles: Record<Exclude<ButtonVariant, 'bare'>, string> = {
  primary: FILLED_DISABLED,
  accent: FILLED_DISABLED,
  danger: FILLED_DISABLED,
  secondary: 'disabled:opacity-50',
  ghost: 'disabled:opacity-50',
  choice: 'disabled:opacity-40',
}

// 컴팩트(애플 44pt 기준): md 44 · sm 36 · lg 48. 글자는 PC 에서 한 단계 키운다(responsive-typography)
const sizeStyles: Record<ButtonSize, string> = {
  sm: 'min-h-9 px-3 py-1.5 text-c13 over10:text-c14',
  md: 'min-h-11 px-4 py-2.5 text-c14 over10:text-c15',
  lg: 'min-h-12 px-6 py-3 text-c15 over10:text-c16',
  /** 아이콘만 있는 버튼 — 정사각형, 접근 가능한 이름(aria-label)은 호출부가 준다 */
  icon: 'size-10 p-0',
  'icon-sm': 'size-8 p-0',
}

const shapeStyles: Record<ButtonShape, string> = {
  field: 'rounded-field',
  pill: 'rounded-full',
}

/**
 * 버튼 클래스 — `<Button>` 과 같은 모양을 링크(`<Link>`) 등 다른 요소에 입힐 때 쓴다.
 * cn() 은 충돌 클래스를 병합하지 않으므로 className 에는 variant·size·shape 와 겹치지 않는 값(폭·여백 등)만 넘긴다.
 */
export function buttonStyles({
  variant = 'primary',
  size = 'md',
  shape = 'pill',
  bold = false,
  skin = 'default',
  className,
}: ButtonStyleOptions = {}) {
  // Custom domain controls keep their existing appearance while sharing the
  // button's hydration, disabled, loading and default-type behavior.
  if (variant === 'bare') return className ?? ''
  return cn(
    // btn · btn-{variant} — 재질(skin) 훅. globals.css 의 버튼 재질 규칙이 이 이름으로 광택 · 빛 · 두께를 입힌다
    'inline-flex cursor-pointer items-center justify-center gap-2 no-underline',
    // 선택 컨트롤은 실행 버튼의 광택·그림자 skin을 적용하지 않는다.
    variant !== 'choice' && 'btn',
    `btn-${variant}`,
    skin === 'flat' && 'btn-flat',
    (size === 'icon' || size === 'icon-sm') && 'btn-icon',
    bold ? 'font-bold' : 'font-medium',
    'transition-[color,background-color,border-color,opacity] duration-200',
    'active:opacity-80 focus:outline-none focus-visible:opacity-90',
    'disabled:cursor-not-allowed',
    disabledStyles[variant],
    variantStyles[variant],
    sizeStyles[size],
    shapeStyles[shape],
    className
  )
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      variant = 'primary',
      size = 'md',
      shape = 'pill',
      bold = false,
      skin = 'default',
      loading = false,
      disabled,
      children,
      className,
      type = 'button',
      ...props
    },
    ref
  ) => {
    const hydrated = useHydrated()
    const isDisabled =
      disabled || loading || (!hydrated && typeof props.onClick === 'function')

    return (
      <button
        ref={ref}
        type={type}
        disabled={isDisabled}
        aria-busy={loading || undefined}
        className={buttonStyles({
          variant,
          size,
          shape,
          bold,
          skin,
          className,
        })}
        {...props}
      >
        {loading && (
          <LuLoaderCircle aria-hidden="true" className="size-4 animate-spin" />
        )}
        {children}
      </button>
    )
  }
)

Button.displayName = 'Button'
